import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrandLogo } from '../hooks/useBrandLogo';
import { 
  Menu, 
  X, 
  Home, 
  FileText, 
  Wrench, 
  Users, 
  Settings, 
  User, 
  LogOut,
  ChevronRight,
  Calculator,
  ShoppingBag,
  Ruler,
  Heart,
  Briefcase,
  Award,
  Gift
} from 'lucide-react';

const MobileHeader = ({ title, showBranding = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const { logoUrl } = useBrandLogo();
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  // Menú siguiendo el flujo comercial
  // Orden: Inicio · Cotizaciones · Ventas · Clientes · CRM · Vendedores · Aliados · Fichas
  const menuItems = [
    { 
      icon: Home, 
      label: 'Inicio', 
      path: '/dashboard',
      show: true
    },
    { 
      icon: Calculator, 
      label: 'Cotizaciones', 
      path: '/quotes',
      show: true
    },
    { 
      icon: ShoppingBag, 
      label: 'Ventas', 
      path: '/ventas',
      show: true
    },
    { 
      icon: Users, 
      label: 'Clientes', 
      path: '/clientes',
      show: true
    },
    { 
      icon: Briefcase, 
      label: 'CRM', 
      path: '/crm',
      show: true
    },
    { 
      icon: Award, 
      label: 'Vendedores', 
      path: '/vendedores',
      show: user?.role === 'gerencia' || user?.role === 'admin'
    },
    { 
      icon: Heart, 
      label: 'Aliados', 
      path: '/aliados',
      show: user?.role === 'gerencia' || user?.role === 'admin'
    },
    {
      icon: Gift,
      label: 'Bodas y Referidos',
      path: '/admin/bodas',
      show: ['gerencia', 'admin', 'asesor', 'vendedor', 'asesor_diseñador'].includes(user?.role)
    },
    { 
      icon: Ruler, 
      label: 'Fichas', 
      path: '/fichas',
      show: true
    },
    { 
      icon: Wrench, 
      label: 'Mantenimiento', 
      path: '/admin/mantenimiento',
      show: user?.role === 'gerencia' || user?.role === 'admin'
    },
    { 
      icon: Settings, 
      label: 'Configuración', 
      path: '/admin/brand',
      show: user?.role === 'gerencia'
    },
    { 
      icon: User, 
      label: 'Mi Perfil', 
      path: '/profile',
      show: true
    }
  ].filter(item => item.show);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Fixed Header - Premium Brand Design with iOS Safe Area */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black md:hidden safe-area-header">
        {/* Main Header Row */}
        <div className="flex items-center justify-between px-4 h-[68px]">
          {/* Logo + Brand Name */}
          <Link to="/dashboard" className="flex items-center gap-3 flex-1 min-w-0">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Juan Pablo Murillo" 
                className="h-11 w-auto object-contain brightness-0 invert flex-shrink-0" 
                data-testid="mobile-brand-logo"
              />
            ) : (
              <div className="w-11 h-11 bg-white/10 animate-pulse flex-shrink-0 rounded-xl" />
            )}
            <div className="min-w-0">
              <h1 className="font-serif text-lg text-white tracking-wide leading-tight truncate">
                ATELIER <span className="text-white/40">—</span> JPM
              </h1>
              <p className="text-[9px] uppercase tracking-[0.12em] text-white/50 truncate">
                Tailoring System
              </p>
            </div>
          </Link>

          {/* Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="w-12 h-12 flex items-center justify-center text-white ml-2 -mr-2 rounded-xl hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-button"
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Context/Title Bar - Only show if title provided */}
        {title && (
          <div className="px-4 pb-3 border-t border-white/10">
            <p className="text-xs text-white/70 pt-2">
              {title}
            </p>
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer - Premium Design with Safe Area */}
      <div 
        className={`fixed top-0 right-0 bottom-0 w-[300px] bg-white z-50 transform transition-transform duration-300 ease-smooth md:hidden shadow-2xl ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Drawer Header with Brand */}
        <div className="bg-black px-5 pt-6 pb-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              {logoUrl && (
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              )}
              <div>
                <p className="font-serif text-base text-white tracking-wide">ATELIER — JPM</p>
                <p className="text-[9px] uppercase tracking-[0.1em] text-white/50">Juan Pablo Murillo</p>
              </div>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white -mr-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info in Header */}
          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
              {user?.profile_photo ? (
                <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-medium text-base">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white text-base truncate">{user?.username}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/50">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="py-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-4 px-5 py-4 transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-warm-100 text-black border-l-3 border-gold'
                  : 'text-warm-600 hover:bg-warm-50 border-l-3 border-transparent'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-black' : 'text-warm-400'}`} />
              <span className="flex-1 text-sm font-medium">{item.label}</span>
              <ChevronRight className={`w-4 h-4 ${isActive(item.path) ? 'text-black' : 'text-warm-300'}`} />
            </Link>
          ))}
        </nav>

        {/* Footer with Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-warm-100 bg-warm-50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 h-12 bg-black text-white hover:bg-warm-800 transition-all duration-200 text-[11px] uppercase tracking-wider font-medium rounded-xl"
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión
          </button>
          <p className="text-center text-[9px] text-warm-400 mt-3 uppercase tracking-wider">
            ATELIER — JPM © 2026
          </p>
        </div>
      </div>

      {/* Spacer for fixed header - includes safe area */}
      <div className={`md:hidden spacer-safe-top ${title ? 'h-[88px]' : 'h-[68px]'}`} />
    </>
  );
};

export default MobileHeader;
